import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

import { phoneCleaner } from "_helpers";

import { statesConst } from "_constants";
import breakpoints from "_constants/breakpoints";

import PhoneMask from "containers/PhoneMask";

const Container = styled.div`
  padding: 36px 32px 36px;
  min-width: 100%;
  max-width: 100%;
  margin-bottom: 33px;
  background: #ffffff;
  box-shadow: 0px 3px 25px rgba(168, 168, 168, 0.3);

  @media (max-width: ${breakpoints.mobileM}) {
    padding: 24px 12px 12px;
  }
`;
const Title = styled.h1`
  width: 100%;
  text-align: center;
  margin: 0;
  margin-bottom: 10px;
  font-family: "Montserrat";
  font-size: 16px;
  line-height: 16px;
  color: #7b848d;
`;
const SubTitle = styled.h2`
  width: 100%;
  margin: 0;
  margin-bottom: 30px;
  text-align: center;
  font-family: "Montserrat";
  font-size: 12px;
  line-height: 12px;
  color: #7b848d;
`;
const Row = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid #ecedf1;
  &:last-child {
    border-bottom: 1px solid #ecedf1;
  }
`;
const DataRow = styled.div`
  display: flex;

  @media (max-width: ${breakpoints.mobileM}) {
    flex-direction: column;
    align-items: center;
    padding: 16px 0px;
    gap: 12px;
    min-height: 64px;
    border-bottom: 1px solid #ecedf1;

    &:nth-child(even) {
      background-color: #fafaff;
    }
  }
`;
const Label = styled.div`
  min-width: 52%;
  width: 52%;
  padding: 8px 14px;
  font-family: "Montserrat";
  font-weight: 700;
  font-size: 10px;
  line-height: 12px;
  color: #7b848d;

  ${DataRow}:first-child & {
    padding-top: 14px;
  }
  ${DataRow}:last-child & {
    padding-bottom: 14px;
  }

  @media (max-width: ${breakpoints.mobileM}) {
    padding: 0px;
    text-align: center;
    font-size: 12px;
    width: 100%;

    ${DataRow}:first-child & {
      padding-top: 0px;
    }
    ${DataRow}:last-child & {
      padding-bottom: 0px;
    }
  }
`;
const Data = styled(Label)`
  min-width: 48%;
  width: 48%;
  padding-left: 24px;
  border-left: 1px solid #ecedf1;
  font-family: "Montserrat";
  font-weight: 500;

  @media (max-width: ${breakpoints.mobileM}) {
    border-left: none;
    padding: 0px;
    line-height: 16px;
    word-wrap: break-word;
    width: 100%;
    font-size: 14px;
  }
`;
const Phone = styled(PhoneMask)`
  border: none;
  padding: 0;
  font-family: "Montserrat";
  font-size: 12px;
  white-space: nowrap;
  color: #7b848d;
  background: none;

  @media (max-width: ${breakpoints.mobileM}) {
    font-size: 14px;
  }
`;

const Divider = styled.div`
  display: none;
  width: 100%;
  height: 1px;
  margin: 12px 0px;
  border-bottom: 2px solid gray;

  @media (max-width: ${breakpoints.mobileM}) {
    display: block;
  }
`;
const EmptyMessage = styled.div`
  color: #7b848d;
  text-align: center;
  font-weight: bold;
`;

function OwnerInfo(props) {
  const { organization, address } = props;

  return (
    <Row>
      {!!organization && (
        <>
          <DataRow>
            <Label>ORGANIZATION</Label>
            <Data>{organization?.name || "—"}</Data>
          </DataRow>

          <DataRow>
            <Label>TYPE</Label>
            <Data>{organization?.type || "—"}</Data>
          </DataRow>

          <DataRow>
            <Label>OTHER TYPE</Label>
            <Data>{organization?.otherType || "—"}</Data>
          </DataRow>

          <DataRow>
            <Label>POSITION</Label>
            <Data>{organization?.position || "—"}</Data>
          </DataRow>

          <DataRow>
            <Label>FORMATION COUNTRY/STATE</Label>
            <Data>{statesConst[organization?.stateOrCountryOfFormation]}</Data>
          </DataRow>
        </>
      )}
      <DataRow>
        <Label>FIRST NAME</Label>
        <Data>{props?.firstName || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>LAST NAME</Label>
        <Data>{props?.lastName || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>EMAIL</Label>
        <Data>{props?.email || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>ADDRESS</Label>
        <Data>{address?.address1 || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>CITIZENSHIP</Label>
        <Data>{props?.countryOfCitizenship || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>STATE</Label>
        <Data>{statesConst[address?.state] || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>CITY</Label>
        <Data>{address?.city || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>ZIP</Label>
        <Data>{address?.zip || "—"}</Data>
      </DataRow>

      <DataRow>
        <Label>PHONE</Label>
        <Data>
          <Phone mask="(999) 999-9999" value={phoneCleaner(props?.phone) || "—"} />
        </Data>
      </DataRow>
    </Row>
  );
}

OwnerInfo.propTypes = {
  index: PropTypes.number,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
  phone: PropTypes.string,
  countryOfCitizenship: PropTypes.string,
  organization: PropTypes.object,
  address: PropTypes.object,
};

function TrademarkOwnersMobile({ individual, organization, coOwners }) {
  return (
    <Container>
      {individual ? (
        <>
          <Title>Trademark Owners</Title>
          <SubTitle>Owners information</SubTitle>
          <OwnerInfo organization={organization} {...individual} />
          {coOwners?.map(({ firstName, lastName }, idx) => (
            <Fragment key={`owner-${idx}`}>
              <Divider />
              <OwnerInfo organization={organization} {...individual} firstName={firstName} lastName={lastName} />
            </Fragment>
          ))}
        </>
      ) : (
        <EmptyMessage>No Trademark Owners info for this owner</EmptyMessage>
      )}
    </Container>
  );
}

TrademarkOwnersMobile.propTypes = {
  individual: PropTypes.object,
  organization: PropTypes.object,
  coOwners: PropTypes.arrayOf(PropTypes.object),
};

export default TrademarkOwnersMobile;
